// What a workspace's chat claimed about the traces on it, for a walkthrough
// to read (lib/investigateAgent.js narrateSession and narrateWorkspace): the
// claims as the board names them (Claim 1, Claim 2, ...), what each rests on, and a
// tool that walks into the chat to see where one came from. The claims are
// leads, as the review's findings are: the walkthrough tells the story from
// the records, and looks into the chat only when it wants to know why a
// claim was made.
//
// PURE: the chat is read through the `readChat` it is handed.
import { Type } from "@earendil-works/pi-ai";

// Claims one walkthrough is told of (the chat's latest), and how long each
// one's words run.
export const WALK_CLAIMS_MAX = 24;
const STATEMENT_CHARS = 400;
const RESTS_MAX = 12;
// What the chat said around a claim, at most.
const QUESTION_CHARS = 600;
const SAID_CHARS = 900;

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();
const clip = (v, n) => {
    const s = str(v).replace(/\s+/g, " ").trim();
    return s.length > n ? `${s.slice(0, n - 3)}...` : s;
};
const text = (body) => ({ content: [{ type: "text", text: body }] });
const splitKey = (key) => {
    const at = str(key).indexOf(":");
    return at > 0 ? [str(key).slice(0, at), str(key).slice(at + 1)] : ["thing", str(key)];
};

// The session each message the graph holds was read in, where a link says.
function messageSessions(graph) {
    const out = new Map();
    const edges = graph?.edges instanceof Map ? graph.edges.values() : [];
    for (const e of edges) {
        const [a, b] = [str(e?.from), str(e?.to)];
        const [msg, ses] = a.startsWith("message:") && b.startsWith("session:") ? [a, b] : b.startsWith("message:") && a.startsWith("session:") ? [b, a] : [null, null];
        if (msg && !out.has(msg)) out.set(msg, lower(splitKey(ses)[1]));
    }
    return out;
}

// The investigation graph's claims as the board numbers them (the latest
// WALK_CLAIMS_MAX), each with what it rests on (sessions by id, a message
// with the session it is in, anything else by its kind and name) and the
// chat's call that made it.
export function walkClaims(graph) {
    const claims = Array.isArray(graph?.claims) ? graph.claims : [];
    const nodes = graph?.nodes instanceof Map ? graph.nodes : new Map();
    const inSession = messageSessions(graph);
    const from = Math.max(0, claims.length - WALK_CLAIMS_MAX);
    return claims.slice(from).map((c, i) => ({
        n: from + i + 1,
        ref: `Claim ${from + i + 1}`,
        id: str(c?.id),
        statement: clip(c?.statement, STATEMENT_CHARS),
        supported: c?.status === "supported",
        rests: [...new Set(Array.isArray(c?.evidence) ? c.evidence.map(str) : [])].slice(0, RESTS_MAX).map((key) => {
            const [kind, id] = splitKey(key);
            return { kind, id, label: nodes.get(key)?.label || null, ...(kind === "message" ? { session: inSession.get(key) || null } : {}) };
        }),
        call: str(c?.source?.toolCallId) || null,
    })).filter((c) => c.statement);
}

// The messages of `session` the claims rest on, for its record to be read
// around them as around the review's citations.
const CLAIM_MESSAGES_MAX = 12;
export function claimMessages(claims, session) {
    const s = lower(session);
    const ids = (claims || []).flatMap((c) => (c.rests || []).filter((r) => r.kind === "message" && r.session === s).map((r) => r.id));
    return [...new Set(ids)].slice(0, CLAIM_MESSAGES_MAX);
}

// How a kind the walkthrough has no name for is counted: one, and many.
const COUNTED = { process: ["a process", "processes"], prompt: ["a prompt", "prompts"], window: ["a time window", "time windows"] };

// What a claim rests on, in a line: the sessions by their refs on the
// workspace (`refOf(id)`), a message by its session's ref and its id,
// machines, people and hosts by name, anything else by how many of each kind.
function restsWords(c, refOf) {
    const out = [];
    const others = new Map();
    for (const r of c.rests) {
        if (r.kind === "session") out.push(refOf(lower(r.id)) || "a session not on the workspace");
        else if (r.kind === "message") {
            const ref = r.session ? refOf(r.session) : null;
            out.push(ref ? `${ref} message [${r.id}]` : r.session ? "a message in a session not on the workspace" : `message [${r.id}]`);
        } else if (r.kind === "endpoint" || r.kind === "person" || r.kind === "host") {
            out.push(`${r.kind === "endpoint" ? "machine" : r.kind} ${clip(r.label || r.id, 60)}`);
        } else others.set(r.kind, (others.get(r.kind) || 0) + 1);
    }
    for (const [kind, n] of others) {
        const [one, many] = COUNTED[kind] || [`a ${kind}`, `${kind}s`];
        out.push(n === 1 ? one : `${n} ${many}`);
    }
    return [...new Set(out)].join(", ");
}

// The brief's lines for the claims: none where there are none.
export function claimsBrief(claims, { refOf = () => null } = {}) {
    if (!claims?.length) return [];
    const left = (claims[0].n || 1) - 1;
    return [
        "",
        "Claims a chat on this workspace made about these sessions, named as the board shows them (leads to check against the records, not the story):",
        ...(left ? [`(Its first ${left} are left out: these are its latest ${claims.length}.)`] : []),
        ...claims.map((c) => {
            const rests = restsWords(c, refOf);
            return `${c.ref} - ${c.supported ? "supported" : "not yet supported"} - ${c.statement}${rests ? ` (rests on ${rests})` : ""}`;
        }),
    ];
}

// A message's words: a string, or its text blocks.
const wordsOf = (m) => (typeof m?.content === "string" ? m.content : (Array.isArray(m?.content) ? m.content : []).filter((b) => b?.type === "text").map((b) => b.text).join(" "));

/**
 * The tool that walks into the chat for where a claim came from:
 * `readChat()` resolves to the chat's messages (pi's agent messages), or null
 * where the chat is not kept here; read once, on the first ask.
 */
export function claimOriginTool({ claims = [], readChat = null } = {}) {
    let messages = null;
    const load = () => (messages ??= Promise.resolve().then(() => (readChat ? readChat() : null)).catch(() => null));
    return {
        name: "investigate_claim_origin",
        label: "Where a claim came from",
        description: "Read, in the chat that made it, how one of the workspace's claims (Claim 1, Claim 2, ...) came about: the question it was answering and what the chat said around the call that made it. Use it only when the records leave you wanting to know why a claim was made; the story comes from the records, not from the chat.",
        parameters: Type.Object({ claim: Type.String({ description: "the claim, as the board names it: Claim 3" }) }),
        async execute(_id, args) {
            const n = Number(/\d+/.exec(str(args?.claim))?.[0]);
            const c = claims.find((x) => x.n === n) || claims.find((x) => lower(x.id) === lower(args?.claim));
            if (!c) return text(`No claim "${clip(args?.claim, 24)}". The claims are ${claims.map((x) => x.ref).join(", ") || "none"}.`);
            const said = `${c.ref} ("${clip(c.statement, 200)}")`;
            const list = await load();
            if (!Array.isArray(list) || !list.length) return text(`The chat that made ${said} is not kept in this browser: tell the story from the records.`);
            const at = c.call ? list.findIndex((m) => m?.role === "assistant" && Array.isArray(m.content) && m.content.some((b) => b?.type === "toolCall" && b.id === c.call)) : -1;
            if (at < 0) return text(`The call that made ${said} is no longer in the chat's history (folded away as it grew, or made by one of its helpers): tell the story from the records.`);
            let asked = -1;
            for (let i = at; i >= 0; i -= 1) if (list[i]?.role === "user" && wordsOf(list[i]).trim()) { asked = i; break; }
            const before = list.slice(asked + 1, at + 1).filter((m) => m?.role === "assistant").map(wordsOf).filter((w) => w.trim()).join(" ");
            // What it said once the call came back, before the next question.
            let after = null;
            for (let i = at + 1; i < list.length && list[i]?.role !== "user"; i += 1) {
                if (list[i]?.role === "assistant" && wordsOf(list[i]).trim()) { after = list[i]; break; }
            }
            const call = list[at].content.find((b) => b?.type === "toolCall" && b.id === c.call);
            return text([
                `${said} was made by the chat${asked >= 0 ? ` while answering: "${clip(wordsOf(list[asked]), QUESTION_CHARS)}"` : ""}.`,
                before ? `What it said before making it: "${clip(before, SAID_CHARS)}"` : "It said nothing before making it.",
                `The call: ${call.name} ${clip(JSON.stringify(call.arguments ?? {}), SAID_CHARS)}`,
                after ? `What it said after: "${clip(wordsOf(after), SAID_CHARS)}"` : "",
                "The chat's words are its reading of the records, not the records: check what matters against the sessions.",
            ].filter(Boolean).join("\n"));
        },
    };
}
